// Spec 16 widget schema (web side). Mirror of the contract in
// apps/api/src/worker/jobs/canvas.ts. Touch one, touch the other.
//
// Key change from v1: the planner emits *what* to render; the
// renderer owns *where*. Canonical render order is fixed:
//   sources_strip → answer_prose → key_facts → followups → diagnostic
//
// `entity_card`s are routed to the right rail on desktop, inline
// after key_facts on narrow viewports.

export interface AnswerProseProps {
	text: string;
	/** Citation refs the prose actually uses inline. The strip is
	 *  authoritative for what's available; this lets the renderer
	 *  highlight matched chips. */
	citation_refs?: number[];
}

export interface SourceItem {
	ref: number;
	id: string;
	type: string; // chunk | entity | community | section | …
	title: string;
	snippet?: string | null;
}

export interface SourcesStripProps {
	sources: SourceItem[];
}

export interface EntityCardProps {
	node_id: string;
	name: string;
	type: string;
	description?: string | null;
	attributes: { key: string; value: string }[];
}

export interface KeyFactsProps {
	title?: string | null;
	rows: { key: string; value: string; emphasis?: boolean | null }[];
}

export interface FollowupsProps {
	items: { label: string; query: string }[];
}

export interface DiagnosticProps {
	mode_selected: string;
	top_k_fetched: number;
	max_score: number;
	hit_breakdown: { chunk: number; entity: number; community: number };
}

export interface ClosestMatchesProps {
	items: { id: string; type: string; score: number; title: string }[];
}

export type Widget =
	| { id: string; kind: "answer_prose"; props: AnswerProseProps }
	| { id: string; kind: "sources_strip"; props: SourcesStripProps }
	| { id: string; kind: "entity_card"; props: EntityCardProps }
	| { id: string; kind: "key_facts"; props: KeyFactsProps }
	| { id: string; kind: "followups"; props: FollowupsProps }
	| { id: string; kind: "diagnostic"; props: DiagnosticProps }
	| { id: string; kind: "closest_matches"; props: ClosestMatchesProps };

export interface CanvasPlan {
	version: 2;
	widgets: Widget[];
}

const KNOWN_KINDS = new Set([
	"answer_prose",
	"sources_strip",
	"entity_card",
	"key_facts",
	"followups",
	"diagnostic",
	"closest_matches",
]);

export function validateCanvas(input: unknown): CanvasPlan | null {
	if (!input || typeof input !== "object") return null;
	const obj = input as Record<string, unknown>;
	if (obj.version !== 2) return null;
	if (!Array.isArray(obj.widgets)) return null;
	const widgets: Widget[] = [];
	for (const w of obj.widgets) {
		if (!w || typeof w !== "object") continue;
		const c = w as Record<string, unknown>;
		if (typeof c.id !== "string") continue;
		if (typeof c.kind !== "string") continue;
		if (!KNOWN_KINDS.has(c.kind)) continue;
		if (!c.props || typeof c.props !== "object") continue;
		widgets.push(c as unknown as Widget);
	}
	return { version: 2, widgets };
}

// ---------------------------------------------------------------------------
// AskResult mirror — only the fields the canvas renderer cares about.

export interface AskResult {
	mode: "global" | "local" | "blended";
	question: string;
	answer: string;
	sources: { id: string; type: string; title: string }[];
	closest_matches?: {
		id: string;
		type: string;
		score: number;
		title: string;
	}[];
	diagnostic?: {
		mode_selected: string;
		top_k_fetched: number;
		max_score: number;
		hit_breakdown: { chunk: number; entity: number; community: number };
		communities_available: number;
	};
	canvas?: CanvasPlan;
}

/**
 * Fallback canvas synthesized from AskResult alone. Used when the
 * planner output is missing or failed schema validation. Stays
 * consistent with the v2 anatomy (sources strip + prose + diagnostic).
 */
export function defaultCanvas(result: AskResult): CanvasPlan {
	const widgets: Widget[] = [];

	if (result.sources.length > 0) {
		widgets.push({
			id: "default-sources",
			kind: "sources_strip",
			props: {
				sources: result.sources.map((s, i) => ({
					ref: i + 1,
					id: s.id,
					type: s.type,
					title: s.title,
					snippet: null,
				})),
			},
		});
	}

	widgets.push({
		id: "default-prose",
		kind: "answer_prose",
		props: { text: result.answer, citation_refs: [] },
	});

	if (result.closest_matches && result.closest_matches.length > 0) {
		widgets.push({
			id: "default-closest",
			kind: "closest_matches",
			props: { items: result.closest_matches },
		});
	}

	if (result.diagnostic) {
		widgets.push({
			id: "default-diag",
			kind: "diagnostic",
			props: {
				mode_selected: result.diagnostic.mode_selected,
				top_k_fetched: result.diagnostic.top_k_fetched,
				max_score: result.diagnostic.max_score,
				hit_breakdown: result.diagnostic.hit_breakdown,
			},
		});
	}

	return { version: 2, widgets };
}

// ---------------------------------------------------------------------------
// Canonical layout: render widgets by kind in fixed order, regardless of
// the order they appear in the planner output. Returns the bucketed
// structure so the renderer can route entity cards to a right rail on
// desktop.

export interface BucketedCanvas {
	sourcesStrip: SourcesStripProps | null;
	prose: AnswerProseProps | null;
	keyFacts: KeyFactsProps[];
	followups: FollowupsProps[];
	entityCards: EntityCardProps[];
	diagnostic: DiagnosticProps | null;
	closestMatches: ClosestMatchesProps[];
}

export function bucketize(plan: CanvasPlan): BucketedCanvas {
	const out: BucketedCanvas = {
		sourcesStrip: null,
		prose: null,
		keyFacts: [],
		followups: [],
		entityCards: [],
		diagnostic: null,
		closestMatches: [],
	};
	for (const w of plan.widgets) {
		switch (w.kind) {
			case "sources_strip":
				out.sourcesStrip = w.props;
				break;
			case "answer_prose":
				out.prose = w.props;
				break;
			case "key_facts":
				out.keyFacts.push(w.props);
				break;
			case "followups":
				out.followups.push(w.props);
				break;
			case "entity_card":
				out.entityCards.push(w.props);
				break;
			case "diagnostic":
				out.diagnostic = w.props;
				break;
			case "closest_matches":
				out.closestMatches.push(w.props);
				break;
		}
	}
	return out;
}
